// ===========================================
// HOOK: Preferencias de Notificaciones (ORR-69 PR 2)
// ===========================================
/**
 * Query y mutation contra la tabla preferencias_notificaciones.
 *
 * La tabla almacena opt-outs por (user_email, tipo, canal). NULL = default
 * (enviar), mantiene comportamiento pre-ORR-69. El trigger enviar_push_async
 * lee estas preferencias para filtrar push del SO por tipo.
 *
 * Default de la UI: todos los switches en "on" cuando no hay fila (ver esCanalActivo).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { toast } from 'sonner'
import { useSupabase } from '@/lib/supabase-context'
import type {
  TipoNotificacion,
  CanalNotificacion,
  PreferenciaNotificacion,
} from '@/core/types/notificaciones'

export const preferenciasNotifKeys = {
  all: ['preferencias-notificaciones'] as const,
  byUser: (email: string) => [...preferenciasNotifKeys.all, email] as const,
}

/**
 * Lista las preferencias del usuario actual.
 * Retorna [] si no hay filas (todos los defaults aplicados via esCanalActivo).
 */
export const usePreferenciasNotificaciones = () => {
  const { user } = useAuth0()
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const userEmail = user?.email ?? ''

  return useQuery({
    queryKey: preferenciasNotifKeys.byUser(userEmail),
    queryFn: async () => {
      if (!supabase || !userEmail) return [] as PreferenciaNotificacion[]
      const { data, error } = await supabase
        .from('preferencias_notificaciones')
        .select('*')
        .eq('user_email', userEmail)

      if (error) throw error
      return (data ?? []) as PreferenciaNotificacion[]
    },
    enabled: !!supabase && !supabaseLoading && !!userEmail,
    // Las preferencias del propio usuario cambian rara vez. 5 min + sin refetch
    // al reenfocar la ventana evita refetcheos redundantes (notorio en móvil,
    // donde el background/foreground es frecuente).
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Upsert de una fila de preferencia con optimistic update.
 *
 * ORR-69 security fix #7: Codex detecto race condition bajo toggles rapidos.
 * La version anterior hacia fire-and-forget sin guard de orden, lo cual
 * podia producir preference drift si el user tocaba 2 switches en <100ms
 * con latencia alta. Fix: optimistic update + rollback on error + invalidate
 * on settled. El cache local refleja el intent del user al instante, las
 * mutations se resuelven en background, y la invalidacion final reconcilia
 * con BD.
 */
export const useToggleCanalPreferencia = () => {
  const { user } = useAuth0()
  const { supabase } = useSupabase()
  const queryClient = useQueryClient()
  const userEmail = user?.email ?? ''

  return useMutation({
    mutationFn: async (input: {
      tipo: TipoNotificacion
      canal: CanalNotificacion
      activo: boolean
    }) => {
      if (!supabase || !user?.email) {
        throw new Error('No autenticado')
      }

      const { error } = await supabase
        .from('preferencias_notificaciones')
        .upsert(
          {
            user_email: user.email,
            tipo: input.tipo,
            canal: input.canal,
            activo: input.activo,
          },
          { onConflict: 'user_email,tipo,canal' },
        )

      if (error) throw error
    },

    // Optimistic update: aplicar el cambio al cache ANTES del network call.
    // Retornamos el snapshot previo para rollback si la mutation falla.
    onMutate: async (input) => {
      const key = preferenciasNotifKeys.byUser(userEmail)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<PreferenciaNotificacion[]>(key)

      queryClient.setQueryData<PreferenciaNotificacion[]>(key, (old = []) => {
        const existingIdx = old.findIndex(
          (p) => p.tipo === input.tipo && p.canal === input.canal,
        )
        if (existingIdx >= 0) {
          const updated = [...old]
          updated[existingIdx] = { ...updated[existingIdx], activo: input.activo }
          return updated
        }
        // Row optimistica con id temporal, sera reemplazada por la real al invalidar
        return [
          ...old,
          {
            id: `optimistic-${input.tipo}-${input.canal}`,
            user_email: userEmail,
            tipo: input.tipo,
            canal: input.canal,
            activo: input.activo,
            created_at: null,
            updated_at: null,
          },
        ]
      })

      return { previous }
    },

    // Rollback al estado anterior si la mutation falla
    onError: (error, _input, context) => {
      console.error('Error actualizando preferencia notif:', error)
      toast.error('Error al guardar preferencia')
      if (context?.previous) {
        queryClient.setQueryData(
          preferenciasNotifKeys.byUser(userEmail),
          context.previous,
        )
      }
    },

    // Reconciliar con BD despues de cualquier resolucion (success o error)
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: preferenciasNotifKeys.byUser(userEmail),
      })
    },
  })
}

/**
 * Helper puro: dado el array de preferencias del user, devuelve si un canal
 * esta activo para un tipo especifico.
 *
 * Default: true (NULL en BD = enviar, mantiene comportamiento pre-ORR-69).
 */
export const esCanalActivo = (
  preferencias: PreferenciaNotificacion[] | undefined,
  tipo: TipoNotificacion,
  canal: CanalNotificacion,
): boolean => {
  if (!preferencias || preferencias.length === 0) return true
  const fila = preferencias.find((p) => p.tipo === tipo && p.canal === canal)
  return fila?.activo ?? true
}
